import { useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Line,
  ComposedChart,
  ResponsiveContainer,
} from 'recharts';
import { Skeleton, Text } from '@musa.tech/design-system';
import { linearRegression } from '@/utils/linearRegression';
import { fmtWeightAxis, fmtWeightStr } from '@/utils/formatWeight';

interface EvolutionPoint {
  period: string;
  co2: number;
}

interface IAEvolutionChartProps {
  data: EvolutionPoint[];
  isLoading?: boolean;
  error?: unknown;
}

const formatYAxis = (value: number) => fmtWeightAxis(value);

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const co2 = payload.find((p: any) => p.dataKey === 'co2');
  return (
    <div className="bg-card border border-border rounded-lg px-3 py-2 shadow-sm">
      <Text variant="label" size="small" className="text-foreground mb-1 block">{label}</Text>
      {co2 && (
        <Text variant="body" size="small" className="text-success-500">
          CO₂ mitigado:{' '}
          <span className="font-semibold">
            {fmtWeightStr(co2.value ?? 0)}
          </span>
        </Text>
      )}
    </div>
  );
};

export const IAEvolutionChart = ({ data, isLoading, error }: IAEvolutionChartProps) => {
  if (isLoading) {
    return <Skeleton className="h-72 w-full rounded-xl" />;
  }

  const isEmpty = !data || data.length === 0;

  const chartData = useMemo(() => {
    if (isEmpty) return [];
    const trendValues = linearRegression(data.map((d) => d.co2));
    return data.map((d, i) => ({ ...d, trend: trendValues[i] }));
  }, [data, isEmpty]);

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <Text variant="heading" size="large" className="text-foreground">
        Evolução do CO₂ mitigado
      </Text>
      <Text variant="body" size="small" className="text-muted-foreground mt-0.5 mb-4 block">
        Acompanhamento mensal do CO₂ mitigado (kg)
      </Text>

      {error ? (
        <div className="flex flex-col items-center justify-center h-52 gap-2">
          <Text variant="body" size="small" className="text-error-500">
            Erro ao carregar dados: {String(error instanceof Error ? error.message : error)}
          </Text>
        </div>
      ) : isEmpty ? (
        <div className="flex flex-col items-center justify-center h-52 text-muted-foreground gap-2">
          <span className="text-3xl">📉</span>
          <Text variant="body" size="small">Sem dados no período selecionado</Text>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
            <defs>
              <linearGradient id="co2Gradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--success-500)" stopOpacity={0.2} />
                <stop offset="95%" stopColor="var(--success-500)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--neutral-200)" />
            <XAxis
              dataKey="period"
              tick={{ fontSize: 11, fill: 'var(--neutral-500)' }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={{ fontSize: 11, fill: 'var(--neutral-500)' }}
              tickLine={false}
              axisLine={false}
              width={40}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="co2"
              stroke="var(--success-500)"
              strokeWidth={2}
              fill="url(#co2Gradient)"
              dot={false}
              activeDot={{ r: 4, fill: 'var(--success-500)' }}
            />
            <Line
              type="linear"
              dataKey="trend"
              stroke="var(--neutral-300)"
              strokeDasharray="4 4"
              strokeWidth={1.5}
              dot={false}
              activeDot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};
